import React, { useMemo } from "react";
import { useMarketStatus, lastSessionLabel } from "../utils/marketStatus";
import { UI, FONT } from "../constants/tokens";
import LTR from "./LTR";
import { fmtPct } from "../utils/formatters";

// ─── התיק היום — the portfolio's move today, in digits ───────────────────────
// The first row of the content on the news, calendar and market-intel
// screens — the ones with no value card. Home and portfolio show the same
// move inside their value card, with the dollar amount, so the line is not
// mounted there (App.js, DAY_MOVE_TABS). Sized as a stated fact, not a
// caption: the number at 22px, the label secondary, reading direction first.
//
// This used to be "קו האופק", a hairline with a marker at the position of the
// day's move on a ±2.5% scale. The scale was a constant nobody chose from a
// source, and the bar quietly asserted what counts as a normal day — a claim
// with no data behind it, in the one place every screen begins. Removed on
// 2026-09-18 (DECISIONS.md); the number, which was always the real value,
// stays. Nothing decorative took the bar's place: empty beats a component
// that needs explaining.
//
// Zero-fabrication: the daily move is computed only from positions that carry
// both a live price and a previous close. When nothing is measurable (guest
// before quotes arrive, empty portfolio, market data missing) the component
// renders nothing at all — no invented zero.
export default function HorizonLine({ portfolio, lang = "he", style = {} }) {
  const market = useMarketStatus();
  const move = useMemo(() => {
    let now = 0, prev = 0;
    for (const p of portfolio || []) {
      if (!(p.shares > 0) || !(p.currentPrice > 0) || !(p.prevClose > 0)) continue;
      now  += p.shares * p.currentPrice;
      prev += p.shares * p.prevClose;
    }
    if (!(prev > 0)) return null;
    return ((now - prev) / prev) * 100;
  }, [portfolio]);

  if (move == null) return null;

  const flat = Math.abs(move) < 0.005;
  // Colour by the fact: gain / loss. A flat day is neither, so it reads in
  // the primary ink — no gold: the flat-day gold was the marker's, and the
  // marker is gone.
  const color = flat ? UI.text.primary : move > 0 ? UI.gain : UI.loss;

  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-start", gap: 10, ...style }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: UI.text.secondary, whiteSpace: "nowrap" }}>
        {lang === "en" ? "Portfolio today" : (market.open ? "התיק היום" : `התיק · ${lastSessionLabel(market)}`)}
      </span>
      <LTR className="si-num" style={{ fontFamily: FONT.num, fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>
        {fmtPct(move)}
      </LTR>
    </div>
  );
}
